<template>
  <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
    <h3 class="text-lg font-medium text-black mb-4">Créer un nouveau paquet</h3>
    
    <form @submit.prevent="handleSubmit" class="space-y-6">
      <!-- Mode de création -->
      <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h4 class="text-lg font-semibold text-blue-900 mb-4">Mode de création</h4>
        <div class="flex space-x-4">
          <label class="flex items-center">
            <input 
              type="radio" 
              v-model="creationMode" 
              value="template"
              class="mr-2"
            />
            <span class="text-sm font-medium text-black">Utiliser un paquet existant</span>
          </label>
          <label class="flex items-center">
            <input 
              type="radio" 
              v-model="creationMode" 
              value="custom"
              class="mr-2"
            />
            <span class="text-sm font-medium text-black">Créer mon propre paquet</span>
          </label>
        </div>
      </div>

      <!-- Section Paquet Existant -->
      <div v-if="creationMode === 'template'" class="bg-gray-50 p-4 rounded-lg border">
        <h4 class="text-lg font-semibold text-black mb-4">Paquet Existant</h4>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Taille du paquet -->
          <div>
            <label for="paquet-taille" class="block text-sm font-medium text-black mb-2">
              Combien avez-vous de tailles ?
            </label>
            <div class="relative">
              <select 
                id="paquet-taille"
                v-model="selectedNbTailles"
                @change="filterPackages"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
                <option value="all">Afficher tout</option>
              </select>
            </div>
          </div>

          <!-- Liste des paquets -->
          <div>
            <label for="paquet-liste" class="block text-sm font-medium text-black mb-2">
              Liste des paquets
            </label>
            <div class="relative">
              <select 
                id="paquet-liste"
                v-model="selectedTemplate"
                @change="loadTemplate"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">-</option>
                <option 
                  v-for="pkg in filteredPackages" 
                  :key="pkg.id"
                  :value="pkg.id"
                >
                  {{ pkg.titre || 'Sans titre' }} - {{ pkg.quantites.join('-') }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Section Création Personnalisée -->
      <div v-if="creationMode === 'custom'" class="bg-gray-50 p-4 rounded-lg border">
        <h4 class="text-lg font-semibold text-black mb-4">Création Personnalisée</h4>

        <div class="mb-6">
          <label for="nombre-tailles" class="block text-sm font-medium text-black mb-2">
            Définir le nombre de taille dans le paquet <span class="text-red-500">*</span>
          </label>
          <select
            id="nombre-tailles"
            v-model.number="selectedNombreTailles"
            @change="clearCustomQuantities"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option :value="null" disabled>Sélectionnez un nombre de tailles</option>
            <option v-for="nombreTailles in 12" :key="nombreTailles" :value="nombreTailles">
              {{ nombreTailles }}
            </option>
          </select>
        </div>
        
        <!-- Titre du paquet -->
        <div class="mb-6">
          <label for="titre" class="block text-sm font-medium text-black mb-2">
            Titre du paquet
          </label>
          <input 
            id="titre"
            v-model="formData.titre"
            type="text"
            placeholder="Ex: Pack Standard, 1-1-1-1-1 (optionnel)"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        
        <!-- Quantités par taille -->
        <div>
          <div class="flex items-center justify-between mb-4">
            <label class="block text-sm font-medium text-black">
              Quantités par taille <span class="text-red-500">*</span>
            </label>
            <div class="text-sm text-black">
              Colonnes p1 à p{{ selectedNombreTailles || 0 }}
            </div>
          </div>
          
          <div class="grid grid-cols-6 gap-3">
            <div 
              v-for="(quantite, index) in visibleQuantityColumns" 
              :key="index"
              class="text-center"
            >
              <label :for="`p${index + 1}`" class="block text-xs font-medium text-black mb-1">
                p{{ index + 1 }}
              </label>
              <div 
                class="px-2 py-1 rounded-md border"
                :class="quantite > 0 ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'"
              >
                <input 
                  :id="`p${index + 1}`"
                  v-model.number="quantityColumns[index]"
                  type="number"
                  min="1"
                  step="1"
                  class="w-full text-center bg-transparent border-none outline-none text-sm font-medium"
                  :class="quantite > 0 ? 'text-green-700' : 'text-black'"
                  @blur="cleanQuantity(index)"
                />
              </div>
            </div>
          </div>
          
          <!-- Message d'erreur pour les quantités -->
          <p v-if="errors.quantites" class="mt-2 text-sm text-red-600">
            {{ errors.quantites }}
          </p>
        </div>
      </div>
      
      <!-- Actions -->
      <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button 
          type="button"
          @click="resetForm"
          class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-black bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          Réinitialiser
        </button>
        <button 
          type="submit"
          :disabled="!isFormValid || isSubmitting"
          class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i v-if="isSubmitting" class="fas fa-spinner fa-spin mr-2"></i>
          <i v-else class="fas fa-plus mr-2"></i>
          {{ isSubmitting ? 'Création...' : 'Créer le paquet' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, reactive } from 'vue'
import type { PackageFormData, PackageItem } from '@/types/pack'

interface Props {
  packagesExistants?: PackageItem[]
}

interface Emits {
  (e: 'submit', data: PackageFormData): void
}

const props = withDefaults(defineProps<Props>(), {
  packagesExistants: () => []
})

const emit = defineEmits<Emits>()

// Mode de création
const creationMode = ref<'template' | 'custom'>('custom')

// Données pour les templates
const selectedNbTailles = ref('all')
const selectedTemplate = ref('')

// Paquets filtrés
const filteredPackages = ref<PackageItem[]>([])

// Filtrer les paquets selon le nombre de tailles sélectionné
const filterPackages = () => {
  selectedTemplate.value = '' // Réinitialiser la sélection
  
  if (selectedNbTailles.value === 'all') {
    filteredPackages.value = props.packagesExistants
  } else {
    const nbTailles = parseInt(selectedNbTailles.value)
    filteredPackages.value = props.packagesExistants.filter(pkg => pkg.nombreTailles === nbTailles)
  }
}

const formData = reactive<PackageFormData>({
  titre: null,
  p1: 0,
  p2: 0,
  p3: 0,
  p4: 0,
  p5: 0,
  p6: 0,
  p7: 0,
  p8: 0,
  p9: 0,
  p10: 0,
  p11: 0,
  p12: 0,
})

// Colonnes de quantités pour la création personnalisée
const selectedNombreTailles = ref<number | null>(null)
const quantityColumns = ref<(number | null)[]>(Array(12).fill(null))
const visibleQuantityColumns = computed(() =>
  quantityColumns.value.slice(0, selectedNombreTailles.value ?? 0)
)

const clearCustomQuantities = () => {
  quantityColumns.value = Array(12).fill(null)
}

const errors = reactive({
  titre: '',
  quantites: ''
})

const isSubmitting = ref(false)

// Vérifier si le formulaire est valide
const isFormValid = computed(() => {
  // En mode template, il suffit d'avoir sélectionné un template
  if (creationMode.value === 'template') {
    return selectedTemplate.value !== ''
  }
  
  // En mode custom, toutes les quantités visibles doivent être renseignées
  if (creationMode.value === 'custom') {
    if (selectedNombreTailles.value === null) return false
    return quantityColumns.value
      .slice(0, selectedNombreTailles.value)
      .every(qty => Number.isInteger(qty) && Number(qty) > 0)
  }
  
  return true
})

// Charger un template existant
const loadTemplate = () => {
  if (selectedTemplate.value) {
    const pkg = props.packagesExistants.find(p => p.id.toString() === selectedTemplate.value)
    if (pkg) {
      formData.titre = pkg.titre || null
      selectedNombreTailles.value = pkg.nombreTailles
      // Remplir les quantités selon le template
      quantityColumns.value = [
        pkg.quantites[0] || 0,
        pkg.quantites[1] || 0,
        pkg.quantites[2] || 0,
        pkg.quantites[3] || 0,
        pkg.quantites[4] || 0,
        pkg.quantites[5] || 0,
        pkg.quantites[6] || 0,
        pkg.quantites[7] || 0,
        pkg.quantites[8] || 0,
        pkg.quantites[9] || 0,
        pkg.quantites[10] || 0,
        pkg.quantites[11] || 0,
      ]
    }
  }
}

// Fonction pour nettoyer une quantité quand l'utilisateur quitte le champ
const cleanQuantity = (index: number) => {
  const value = quantityColumns.value[index]
  if (value === null || value === undefined || isNaN(Number(value))) {
    quantityColumns.value[index] = null
  } else {
    const numericValue = Number(value)
    quantityColumns.value[index] = Number.isInteger(numericValue) && numericValue > 0
      ? numericValue
      : null
  }
}

const handleSubmit = async () => {
  if (!isFormValid.value) return

  isSubmitting.value = true
  
  try {
    // Créer l'objet avec le titre et les 12 positions de quantités
    const dataToSubmit = {
      titre: formData.titre || null,
      p1: quantityColumns.value[0] || 0,
      p2: quantityColumns.value[1] || 0,
      p3: quantityColumns.value[2] || 0,
      p4: quantityColumns.value[3] || 0,
      p5: quantityColumns.value[4] || 0,
      p6: quantityColumns.value[5] || 0,
      p7: quantityColumns.value[6] || 0,
      p8: quantityColumns.value[7] || 0,
      p9: quantityColumns.value[8] || 0,
      p10: quantityColumns.value[9] || 0,
      p11: quantityColumns.value[10] || 0,
      p12: quantityColumns.value[11] || 0,
    }
    
    emit('submit', dataToSubmit)
    
    resetForm()
  } catch (error) {
    console.error('Erreur lors de la création:', error)
  } finally {
    isSubmitting.value = false
  }
}

const resetForm = () => {
  formData.titre = null
  selectedNombreTailles.value = null
  quantityColumns.value = Array(12).fill(null)
  selectedTemplate.value = ''
  selectedNbTailles.value = 'all'
  creationMode.value = 'custom'
  errors.titre = ''
  errors.quantites = ''
}

// Initialiser les paquets filtrés
watch(() => props.packagesExistants, () => {
  filterPackages()
}, { immediate: true })
</script>
